import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { SunIcon, MoonIcon, MenuIcon, XIcon, ChevronDownIcon, UserIcon, HomeIcon, BookOpenIcon, UsersIcon, BookIcon, MessageSquareIcon, StarIcon, BriefcaseIcon, VolumeIcon, LogOutIcon, LogInIcon, SearchIcon, GraduationCapIcon, ShoppingBagIcon, NewspaperIcon, ClockIcon, ThumbsUpIcon } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { AuthModal } from './AuthModal';
import { Logo } from './Logo';
interface NavItem {
  label: {
    en: string;
    ar: string;
  };
  href: string;
  icon?: React.ElementType;
  comingSoon?: boolean;
  children?: {
    label: {
      en: string;
      ar: string;
    };
    description?: {
      en: string;
      ar: string;
    };
    href: string;
    icon?: React.ElementType;
    featured?: boolean;
    comingSoon?: boolean;
  }[];
}
const NAV_ITEMS: NavItem[] = [{
  label: {
    en: 'Learn',
    ar: 'تعلم'
  },
  href: '/learn',
  icon: BookOpenIcon,
  children: [{
    label: {
      en: 'Learning Path',
      ar: 'مسار التعلم'
    },
    description: {
      en: 'Personalized learning journey',
      ar: 'رحلة تعليمية مخصصة'
    },
    href: '/learn/path',
    icon: GraduationCapIcon,
    featured: true
  }, {
    label: {
      en: 'Video Lessons',
      ar: 'دروس الفيديو'
    },
    description: {
      en: 'Learn with interactive videos',
      ar: 'تعلم مع مقاطع الفيديو التفاعلية'
    },
    href: '/learn/video-lessons',
    icon: BookOpenIcon
  }, {
    label: {
      en: 'Practice Exercises',
      ar: 'تمارين تطبيقية'
    },
    description: {
      en: 'Test your knowledge',
      ar: 'اختبر معرفتك'
    },
    href: '/learn/practice',
    icon: BookIcon
  }]
}, {
  label: {
    en: 'Resources',
    ar: 'المصادر'
  },
  href: '/resources',
  icon: BookIcon,
  children: [{
    label: {
      en: "Beginner's Guide",
      ar: 'دليل المبتدئين'
    },
    description: {
      en: 'Start your English journey here',
      ar: 'ابدأ رحلة تعلم الإنجليزية هنا'
    },
    href: '/resources/beginners-guide',
    icon: StarIcon,
    featured: true
  }, {
    label: {
      en: 'Business English',
      ar: 'الإنجليزية للأعمال'
    },
    description: {
      en: 'Professional vocabulary and phrases',
      ar: 'المفردات والعبارات المهنية'
    },
    href: '/resources/business-english',
    icon: BriefcaseIcon,
    featured: true
  }, {
    label: {
      en: 'Recommended Resources',
      ar: 'الموارد الموصى بها'
    },
    description: {
      en: 'Books, courses, and tools we recommend',
      ar: 'الكتب والدورات والأدوات التي نوصي بها'
    },
    href: '/resources/recommended',
    icon: ThumbsUpIcon
  }, {
    label: {
      en: 'Vocabulary Lists',
      ar: 'قوائم المفردات'
    },
    description: {
      en: 'Essential words with examples',
      ar: 'كلمات أساسية مع أمثلة'
    },
    href: '/resources/vocabulary',
    icon: BookIcon
  }, {
    label: {
      en: 'Grammar Guide',
      ar: 'دليل القواعد'
    },
    description: {
      en: 'Learn English grammar rules',
      ar: 'تعلم قواعد اللغة الإنجليزية'
    },
    href: '/resources/grammar',
    icon: BookOpenIcon
  }, {
    label: {
      en: 'Pronunciation',
      ar: 'النطق'
    },
    description: {
      en: 'Master English sounds',
      ar: 'أتقن أصوات اللغة الإنجليزية'
    },
    href: '/resources/pronunciation',
    icon: VolumeIcon
  }]
}, {
  label: {
    en: 'Community',
    ar: 'المجتمع'
  },
  href: '/community',
  icon: UsersIcon,
  children: [{
    label: {
      en: 'Discussion Forum',
      ar: 'منتدى النقاش'
    },
    description: {
      en: 'Connect with other learners',
      ar: 'تواصل مع متعلمين آخرين'
    },
    href: '/community',
    icon: MessageSquareIcon
  }]
}, {
  label: {
    en: 'Blog',
    ar: 'المدونة'
  },
  href: '/blog',
  icon: NewspaperIcon
}, {
  label: {
    en: 'Shop',
    ar: 'المتجر'
  },
  href: '#',
  icon: ShoppingBagIcon,
  comingSoon: true
}];
export function Navbar() {
  const {
    theme,
    toggleTheme
  } = useTheme();
  const {
    isArabic,
    toggleLanguage
  } = useLanguage();
  const {
    isAuthenticated,
    user,
    logout
  } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  useEffect(() => {
    setMobileMenuOpen(false);
    setActiveDropdown(null);
  }, [location]);
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 10) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  const toggleDropdown = (label: string) => {
    setActiveDropdown(activeDropdown === label ? null : label);
  };
  const handleLogin = () => {
    setAuthModalOpen(true);
  };
  const handleLogout = () => {
    logout();
  };
  return <>
      <header className={`bg-white dark:bg-gray-900 sticky top-0 z-50 ${scrolled ? 'shadow-md border-b border-gray-200 dark:border-gray-800' : 'shadow-sm border-b border-gray-100 dark:border-gray-800/50'} transition-all duration-200`} data-id="element-1006">
        <div className="max-w-7xl mx-auto" data-id="element-1007">
          <div className="flex justify-between items-center h-16 px-4" data-id="element-1008">
            <Link to="/" className="flex items-center" data-id="element-1009">
              <Logo width={32} height={32} color={theme === 'dark' ? '#60A5FA' : '#3B82F6'} data-id="element-1010" />
              <span className="text-xl font-bold text-gray-900 dark:text-white ml-2 hidden md:block" data-id="element-1011">
                Arabicwithenglish
              </span>
            </Link>
            <nav className="hidden md:flex items-center space-x-1" data-id="element-1012">
              <div className="hidden lg:flex items-center mr-4" data-id="element-1013">
                {NAV_ITEMS.map(item => <div key={item.href} className="relative group px-1" data-id="element-1014">
                    {item.children ? <button onClick={() => toggleDropdown(item.label.en)} className={`flex items-center gap-1.5 px-3 py-2 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors ${activeDropdown === item.label.en ? 'bg-gray-100 dark:bg-gray-800 text-blue-600 dark:text-blue-400' : ''}`} data-id="element-1015">
                        {item.icon && <item.icon className="w-5 h-5 text-gray-500 dark:text-gray-400" data-id="element-1016" />}
                        <span data-id="element-1017">{isArabic ? item.label.ar : item.label.en}</span>
                        {item.comingSoon && <span className="ml-1 inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400" data-id="element-1018">
                            <ClockIcon className="w-3 h-3 mr-0.5" data-id="element-1019" />
                            {isArabic ? 'قريبًا' : 'Soon'}
                          </span>}
                        <ChevronDownIcon className={`w-4 h-4 transition-transform ${activeDropdown === item.label.en ? 'rotate-180' : ''}`} data-id="element-1020" />
                      </button> : <Link to={item.comingSoon ? '#' : item.href} className="flex items-center gap-1.5 px-3 py-2 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors" onClick={item.comingSoon ? e => e.preventDefault() : undefined} data-id="element-1021">
                        {item.icon && <item.icon className="w-5 h-5 text-gray-500 dark:text-gray-400" data-id="element-1022" />}
                        <span data-id="element-1023">{isArabic ? item.label.ar : item.label.en}</span>
                        {item.comingSoon && <span className="ml-1 inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400" data-id="element-1024">
                            <ClockIcon className="w-3 h-3 mr-0.5" data-id="element-1025" />
                            {isArabic ? 'قريبًا' : 'Soon'}
                          </span>}
                      </Link>}
                    {item.children && activeDropdown === item.label.en && <div className="absolute left-0 mt-2 w-64 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 overflow-hidden z-20" data-id="element-1026">
                        <div className="p-4" data-id="element-1027">
                          <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-2" data-id="element-1028">
                            {isArabic ? item.label.ar : item.label.en}
                          </h3>
                          <div className="space-y-2" data-id="element-1029">
                            {item.children.map(child => <Link key={child.href} to={child.comingSoon ? '#' : child.href} className="flex items-start gap-3 p-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors" onClick={child.comingSoon ? e => e.preventDefault() : undefined} data-id="element-1030">
                                {child.icon && <div className="mt-0.5" data-id="element-1031">
                                    <child.icon className="w-5 h-5 text-blue-600 dark:text-blue-400" data-id="element-1032" />
                                  </div>}
                                <div data-id="element-1033">
                                  <div className="flex items-center gap-1.5 font-medium text-gray-900 dark:text-white" data-id="element-1034">
                                    {isArabic ? child.label.ar : child.label.en}
                                    {child.comingSoon && <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400" data-id="element-1035">
                                        <ClockIcon className="w-3 h-3 mr-0.5" data-id="element-1036" />
                                        {isArabic ? 'قريبًا' : 'Soon'}
                                      </span>}
                                  </div>
                                  {child.description && <div className="text-xs text-gray-500 dark:text-gray-400" data-id="element-1037">
                                      {isArabic ? child.description.ar : child.description.en}
                                    </div>}
                                </div>
                              </Link>)}
                          </div>
                        </div>
                      </div>}
                  </div>)}
              </div>
              {!isAuthenticated ? <button onClick={handleLogin} className="hidden lg:flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors" data-id="element-1038">
                  <LogInIcon className="w-4 h-4" data-id="element-1039" />
                  <span data-id="element-1040">{isArabic ? 'تسجيل الدخول' : 'Sign In'}</span>
                </button> : <div className="hidden lg:block relative" data-id="element-1041">
                  <button onClick={() => toggleDropdown('user')} className="flex items-center gap-2 px-3 py-2 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800" data-id="element-1042">
                    <div className="w-8 h-8 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center" data-id="element-1043">
                      <UserIcon className="w-5 h-5 text-blue-600 dark:text-blue-400" data-id="element-1044" />
                    </div>
                    <span className="hidden xl:block" data-id="element-1045">
                      {user?.displayName || 'User'}
                    </span>
                    <ChevronDownIcon className={`w-4 h-4 transition-transform ${activeDropdown === 'user' ? 'rotate-180' : ''}`} data-id="element-1046" />
                  </button>
                  {activeDropdown === 'user' && <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 overflow-hidden z-20" data-id="element-1047">
                      <div className="py-1" data-id="element-1048">
                        <Link to="/profile" className="flex items-center gap-2 px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700" data-id="element-1049">
                          <UserIcon className="w-4 h-4" data-id="element-1050" />
                          <span data-id="element-1051">{isArabic ? 'الملف الشخصي' : 'Profile'}</span>
                        </Link>
                        <button onClick={handleLogout} className="flex items-center gap-2 px-4 py-2 w-full text-left text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20" data-id="element-1052">
                          <LogOutIcon className="w-4 h-4" data-id="element-1053" />
                          <span data-id="element-1054">{isArabic ? 'تسجيل الخروج' : 'Logout'}</span>
                        </button>
                      </div>
                    </div>}
                </div>}
              <button onClick={toggleLanguage} className="p-2 rounded-md text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800" aria-label="Toggle language" data-id="element-1055">
                <div className="flex items-center gap-1.5" data-id="element-1056">
                  <span className="font-medium text-sm" data-id="element-1057">
                    {isArabic ? 'EN' : 'عربي'}
                  </span>
                </div>
              </button>
              <button className="p-2 rounded-md text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800" aria-label="Search" data-id="element-1058">
                <SearchIcon className="h-5 w-5" data-id="element-1059" />
              </button>
              <button onClick={toggleTheme} className="p-2 rounded-md text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800" aria-label="Toggle theme" data-id="element-1060">
                {theme === 'dark' ? <MoonIcon className="h-5 w-5" data-id="element-1061" /> : <SunIcon className="h-5 w-5" data-id="element-1062" />}
              </button>
              <button onClick={() => setMobileMenuOpen(true)} className="lg:hidden p-2 rounded-md text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 focus:outline-none" aria-label="Open menu" data-id="element-1063">
                <MenuIcon className="h-6 w-6" data-id="element-1064" />
              </button>
            </nav>
            <div className="flex items-center gap-2 md:hidden" data-id="element-1065">
              <Link to="/" className="p-2 rounded-md text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800" aria-label="Home" data-id="element-1066">
                <HomeIcon className="h-5 w-5" data-id="element-1067" />
              </Link>
              <button onClick={toggleLanguage} className="p-2 rounded-md text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800" aria-label="Toggle language" data-id="element-1068">
                <span className="font-medium text-sm" data-id="element-1069">
                  {isArabic ? 'EN' : 'عربي'}
                </span>
              </button>
              <button className="p-2 rounded-md text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800" aria-label="Search" data-id="element-1070">
                <SearchIcon className="h-5 w-5" data-id="element-1071" />
              </button>
              <button onClick={toggleTheme} className="p-2 rounded-md text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800" aria-label="Toggle theme" data-id="element-1072">
                {theme === 'dark' ? <MoonIcon className="h-5 w-5" data-id="element-1073" /> : <SunIcon className="h-5 w-5" data-id="element-1074" />}
              </button>
              <button onClick={() => setMobileMenuOpen(true)} className="p-2 rounded-md text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 focus:outline-none" aria-label="Open menu" data-id="element-1075">
                <MenuIcon className="h-6 w-6" data-id="element-1076" />
              </button>
            </div>
          </div>
        </div>
      </header>
      <div className={`fixed inset-0 z-50 bg-black/50 backdrop-blur-sm transition-opacity ${mobileMenuOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}`} onClick={() => setMobileMenuOpen(false)} data-id="element-1077">
        <div className={`fixed inset-y-0 left-0 w-full max-w-xs bg-white dark:bg-gray-900 shadow-xl transition-transform duration-300 ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'}`} onClick={e => e.stopPropagation()} data-id="element-1078">
          <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-800" data-id="element-1079">
            <div className="flex items-center" data-id="element-1080">
              <Logo width={32} height={32} color={theme === 'dark' ? '#60A5FA' : '#3B82F6'} data-id="element-1081" />
              <span className="text-xl font-bold text-gray-900 dark:text-white ml-2" data-id="element-1082">
                Arabicwithenglish
              </span>
            </div>
            <button onClick={() => setMobileMenuOpen(false)} className="p-2 rounded-full text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 focus:outline-none" data-id="element-1083">
              <XIcon className="h-6 w-6" data-id="element-1084" />
            </button>
          </div>
          <nav className="p-4" data-id="element-1085">
            <div className="space-y-1" data-id="element-1086">
              {NAV_ITEMS.map(item => <div key={item.href} data-id="element-1087">
                  {item.children ? <div data-id="element-1088">
                      <button onClick={() => toggleDropdown(item.label.en)} className="w-full flex items-center justify-between py-3 px-4 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors" data-id="element-1089">
                        <div className="flex items-center gap-3" data-id="element-1090">
                          {item.icon && <item.icon className="w-6 h-6 text-gray-500 dark:text-gray-400" data-id="element-1091" />}
                          <span className="font-medium" data-id="element-1092">
                            {isArabic ? item.label.ar : item.label.en}
                          </span>
                          {item.comingSoon && <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400" data-id="element-1093">
                              <ClockIcon className="w-3 h-3 mr-0.5" data-id="element-1094" />
                              {isArabic ? 'قريبًا' : 'Soon'}
                            </span>}
                        </div>
                        <ChevronDownIcon className={`w-5 h-5 text-gray-500 dark:text-gray-400 transition-transform ${activeDropdown === item.label.en ? 'rotate-180' : ''}`} data-id="element-1095" />
                      </button>
                      {activeDropdown === item.label.en && <div className="pl-12 space-y-1 mt-1" data-id="element-1096">
                          {item.children.map(child => <Link key={child.href} to={child.comingSoon ? '#' : child.href} className="flex items-center gap-2 py-2 px-4 rounded-lg text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors" onClick={e => {
                    if (child.comingSoon) e.preventDefault();else setMobileMenuOpen(false);
                  }} data-id="element-1097">
                              {child.icon && <child.icon className="w-5 h-5 text-gray-500 dark:text-gray-400" data-id="element-1098" />}
                              <span data-id="element-1099">
                                {isArabic ? child.label.ar : child.label.en}
                              </span>
                              {child.comingSoon && <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400" data-id="element-1100">
                                  <ClockIcon className="w-3 h-3 mr-0.5" data-id="element-1101" />
                                  {isArabic ? 'قريبًا' : 'Soon'}
                                </span>}
                            </Link>)}
                        </div>}
                    </div> : <Link to={item.comingSoon ? '#' : item.href} className="flex items-center gap-3 py-3 px-4 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors" onClick={e => {
                if (item.comingSoon) e.preventDefault();else setMobileMenuOpen(false);
              }} data-id="element-1102">
                      {item.icon && <item.icon className="w-6 h-6 text-gray-500 dark:text-gray-400" data-id="element-1103" />}
                      <span className="font-medium" data-id="element-1104">
                        {isArabic ? item.label.ar : item.label.en}
                      </span>
                      {item.comingSoon && <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400" data-id="element-1105">
                          <ClockIcon className="w-3 h-3 mr-0.5" data-id="element-1106" />
                          {isArabic ? 'قريبًا' : 'Soon'}
                        </span>}
                    </Link>}
                </div>)}
            </div>
          </nav>
          <div className="p-4 mt-4" data-id="element-1107">
            {isAuthenticated ? <div className="space-y-3" data-id="element-1108">
                <Link to="/profile/settings" className="flex items-center gap-3 py-3 px-4 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors" onClick={() => setMobileMenuOpen(false)} data-id="element-1109">
                  <UserIcon className="w-6 h-6 text-gray-500 dark:text-gray-400" data-id="element-1110" />
                  <span className="font-medium" data-id="element-1111">
                    {isArabic ? 'الملف الشخصي' : 'Profile'}
                  </span>
                </Link>
                <button onClick={handleLogout} className="w-full flex items-center gap-3 py-3 px-4 rounded-lg text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors text-left" data-id="element-1112">
                  <LogOutIcon className="w-6 h-6" data-id="element-1113" />
                  <span className="font-medium" data-id="element-1114">
                    {isArabic ? 'تسجيل الخروج' : 'Logout'}
                  </span>
                </button>
              </div> : <button onClick={handleLogin} className="w-full flex items-center justify-center gap-2 py-3 px-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors" data-id="element-1115">
                <LogInIcon className="w-5 h-5" data-id="element-1116" />
                <span className="font-medium" data-id="element-1117">
                  {isArabic ? 'تسجيل الدخول' : 'Sign In'}
                </span>
              </button>}
          </div>
          <div className="p-4 border-t border-gray-200 dark:border-gray-800" data-id="element-1118">
            <div className="grid grid-cols-2 gap-4" data-id="element-1119">
              <button onClick={toggleLanguage} className="flex items-center justify-center gap-2 py-3 px-4 rounded-lg border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300" data-id="element-1120">
                <span className="font-medium" data-id="element-1121">
                  {isArabic ? 'English' : 'عربي'}
                </span>
              </button>
              <button onClick={toggleTheme} className="flex items-center justify-center gap-2 py-3 px-4 rounded-lg border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300" data-id="element-1122">
                {theme === 'dark' ? <>
                    <SunIcon className="w-5 h-5 text-gray-500 dark:text-gray-400" data-id="element-1123" />
                    <span className="font-medium" data-id="element-1124">
                      {isArabic ? 'وضع النهار' : 'Light Mode'}
                    </span>
                  </> : <>
                    <MoonIcon className="w-5 h-5 text-gray-500 dark:text-gray-400" data-id="element-1125" />
                    <span className="font-medium" data-id="element-1126">
                      {isArabic ? 'الوضع المظلم' : 'Dark Mode'}
                    </span>
                  </>}
              </button>
            </div>
          </div>
        </div>
      </div>
      <AuthModal isOpen={authModalOpen} onClose={() => setAuthModalOpen(false)} data-id="element-1127" />
    </>;
}